// components/layout/PullToRefresh.tsx — Wraps the app's main scroll
// container with a native-style pull-to-refresh gesture.
//
// Accessibility: dragging is a path-based gesture, so WCAG 2.5.1 requires a
// single-pointer/keyboard alternative — a real <button> is always present
// (visually hidden until focused, like a skip link) and does the same
// refresh. Gesture status is also announced via an aria-live region, and
// `prefers-reduced-motion` is handled globally in styles/animations.css.

import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import styles from './PullToRefresh.module.css';

interface PullToRefreshProps {
  onRefresh: () => Promise<void> | void;
  disabled?: boolean;
  className?: string | undefined;
  id?: string;
  children: ReactNode;
}

export function PullToRefresh({ onRefresh, disabled = false, className, id, children }: PullToRefreshProps) {
  const { t } = useTranslation('common');
  const { containerRef, status, pullDistance, refresh } = usePullToRefresh({ onRefresh, disabled });

  const statusMessage =
    status === 'refreshing' ? t('pullToRefresh.refreshing')
    : status === 'ready'      ? t('pullToRefresh.release')
    : status === 'pulling'    ? t('pullToRefresh.pull')
    : '';

  return (
    <main ref={containerRef} className={className} {...(id ? { id } : {})}>
      {!disabled && (
        <>
          <button type="button" className={styles.a11yTrigger} onClick={() => void refresh()}>
            {t('pullToRefresh.action')}
          </button>

          <div
            className={styles.indicatorTrack}
            style={{
              height: pullDistance,
              transition: status === 'pulling' ? 'none' : undefined,
            }}
            aria-hidden="true"
          >
            <span
              className={clsx(styles.spinner, status === 'refreshing' && styles.spinning)}
              style={status === 'refreshing' ? undefined : { transform: `rotate(${(pullDistance / 64) * 180}deg)` }}
            />
          </div>

          <span className="sr-only" role="status" aria-live="polite">
            {statusMessage}
          </span>
        </>
      )}

      {children}
    </main>
  );
}
